import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useCartStore } from '../store/cartStore';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name A–Z' },
  { value: 'name-desc', label: 'Name Z–A' },
  { value: 'price-asc', label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'stock-desc', label: 'In Stock First' },
];

const COLLECTION_TABS = [
  { handle: 'all-products', label: 'All Products' },
  { handle: 'mattresses', label: 'Mattresses' },
  { handle: 'pillow', label: 'Pillows' },
  { handle: 'bedding', label: 'Bedding' },
  { handle: 'bedsheets', label: 'Bedsheets' },
  { handle: 'support-mobility', label: 'Support & Mobility' },
  { handle: 'petbed', label: 'Dog Beds' },
  { handle: 'dog-bed-covers', label: 'Dog Bed Covers' },
  { handle: 'bed', label: 'Beds' },
  { handle: 'recliner', label: 'Recliner' },
];

function ProductSearch({ locationId, refreshKey }) {
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [addedVariantId, setAddedVariantId] = useState(null);
  const [activeTab, setActiveTab] = useState('all-products');
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [customizeProduct, setCustomizeProduct] = useState(null);
  const [customizeVariant, setCustomizeVariant] = useState(null);
  const [customLength, setCustomLength] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [standardDims, setStandardDims] = useState(null);
  const [matchedVariant, setMatchedVariant] = useState(null);
  const [matchedDims, setMatchedDims] = useState(null);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [personalizeProduct, setPersonalizeProduct] = useState(null);
  const [personalizeVariant, setPersonalizeVariant] = useState(null);
  const [personalizeName, setPersonalizeName] = useState('');
  const [wantsPersonalization, setWantsPersonalization] = useState(false);
  const [mtoMap, setMtoMap] = useState({});
  const [locationType, setLocationType] = useState('');
  const [locationName, setLocationName] = useState('');
  const { addToCart } = useCartStore();

  const fetchControllerRef = React.useRef(null);
  const mtoControllerRef = React.useRef(null);
  const searchLogTimerRef = useRef(null);

  const logProductSearch = useCallback((query, resultCount) => {
    if (searchLogTimerRef.current) clearTimeout(searchLogTimerRef.current);
    if (!query || query.length < 2) return;
    searchLogTimerRef.current = setTimeout(() => {
      api.post('/events/search-log', { query, resultsCount: resultCount, source: 'product' }).catch(() => {});
    }, 800);
  }, []);

  useEffect(() => {
    fetchProducts(activeTab);
    return () => {
      if (fetchControllerRef.current) fetchControllerRef.current.abort();
      if (mtoControllerRef.current) mtoControllerRef.current.abort();
      if (inventoryControllerRef.current) inventoryControllerRef.current.abort();
    };
  }, [locationId, activeTab, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch location type (store vs warehouse) for MTO logic
  useEffect(() => {
    if (!locationId) return;
    setLocationType(''); // Reset immediately to prevent stale type from old location
    api.get('/locations').then(res => {
      const locs = res.data.locations || [];
      const current = locs.find(l => l.id?.toString() === locationId?.toString());
      setLocationType((current?.type || 'store').toLowerCase());
      setLocationName(current?.name || '');
    }).catch(() => { setLocationType('store'); setLocationName(''); });
  }, [locationId]);

  // Fetch MTO status in background after products load (skip for store locations)
  useEffect(() => {
    if (allProducts.length === 0 || locationType === 'store') {
      if (locationType === 'store') setMtoMap({});
      return;
    }
    // Wait for locationType to resolve before fetching
    if (!locationType) return;
    if (mtoControllerRef.current) mtoControllerRef.current.abort();
    const controller = new AbortController();
    mtoControllerRef.current = controller;

    const continueVariantIds = [];
    allProducts.forEach(p => {
      p.variants.forEach(v => {
        if (v.inventoryPolicy === 'continue') continueVariantIds.push(v.id);
      });
    });
    if (continueVariantIds.length === 0) return;

    // Batch fetch in chunks of 250 (GraphQL nodes limit)
    (async () => {
      for (let i = 0; i < continueVariantIds.length; i += 250) {
        if (controller.signal.aborted) return;
        const chunk = continueVariantIds.slice(i, i + 250);
        try {
          const res = await api.post('/products/variants/batch-is-mto', { variantIds: chunk }, { signal: controller.signal });
          if (!controller.signal.aborted) {
            setMtoMap(prev => ({ ...prev, ...res.data.results }));
          }
        } catch (e) {
          if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
            console.error('Error fetching MTO batch:', e);
          }
        }
      }
    })();
  }, [allProducts, locationType]);

  const [inventoryLoading, setInventoryLoading] = useState(false);
  const inventoryControllerRef = useRef(null);

  const fetchProducts = async (handle) => {
    // Cancel any in-flight request
    if (fetchControllerRef.current) fetchControllerRef.current.abort();
    if (inventoryControllerRef.current) inventoryControllerRef.current.abort();
    const controller = new AbortController();
    fetchControllerRef.current = controller;

    try {
      setLoading(true);
      setMtoMap({});
      const params = { ...(locationId && { locationId }) };
      let response;
      if (handle === 'all-products') {
        response = await api.get('/products', { params: { ...params, limit: 250, status: 'active' }, signal: controller.signal });
      } else {
        response = await api.get(`/products/collection/${handle}`, { params: { ...params, status: 'active' }, signal: controller.signal });
      }
      if (!controller.signal.aborted) {
        // When locationId is set, mark inventory as pending (null) until location-specific fetch completes
        const products = response.data.products;
        if (locationId) {
          products.forEach(p => p.variants.forEach(v => { v.inventoryQuantity = null; }));
        }
        setAllProducts(products);
        setLoading(false);

        // Fetch inventory in background if locationId is set
        if (locationId) {
          const products = response.data.products;
          const itemIds = [];
          const itemToVariant = {};
          products.forEach(p => p.variants.forEach(v => {
            if (v.inventoryItemId) {
              itemIds.push(v.inventoryItemId);
              itemToVariant[v.inventoryItemId] = v.id;
            }
          }));
          if (itemIds.length > 0) {
            setInventoryLoading(true);
            const invController = new AbortController();
            inventoryControllerRef.current = invController;
            try {
              const invRes = await api.post('/products/inventory-levels', { locationId, inventoryItemIds: itemIds }, { signal: invController.signal });
              if (!invController.signal.aborted && invRes.data.inventory) {
                const invMap = invRes.data.inventory;
                setAllProducts(prev => prev.map(p => ({
                  ...p,
                  variants: p.variants.map(v => {
                    if (v.inventoryItemId) {
                      // If in map, use location qty; if not in map, not stocked here → 0
                      return { ...v, inventoryQuantity: invMap[v.inventoryItemId] ?? 0 };
                    }
                    return { ...v, inventoryQuantity: 0 };
                  })
                })));
              }
            } catch (e) {
              if (e.name !== 'AbortError' && e.code !== 'ERR_CANCELED') {
                console.error('Error fetching inventory:', e);
              }
            }
            setInventoryLoading(false);
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error fetching products:', error);
        setAllProducts([]);
        setLoading(false);
      }
    }
  };

  const handleTabChange = (handle) => {
    setActiveTab(handle);
    setSearchQuery('');
  };

  // Flatten into variant cards, client-side filter by product title / variant title / SKU / barcode
  const flatItems = useMemo(() => {
    const items = [];
    allProducts.forEach(product => {
      product.variants.forEach(variant => {
        items.push({ product, variant });
      });
    });

    // Client-side search — split query into words, all must match (wildcard)
    // Strip apostrophes/special chars for fuzzy matching (e.g. "Founders" matches "Founder's")
    const filtered = (!searchQuery || searchQuery.length < 2)
      ? items
      : items.filter(({ product, variant }) => {
          const normalize = (s) => s.toLowerCase().replace(/[''`]/g, '');
          const words = normalize(searchQuery).split(/\s+/).filter(Boolean);
          const searchText = normalize([
            product.title,
            variant.title,
            variant.sku,
            variant.barcode,
            product.productType,
            product.tags
          ].filter(Boolean).join(' '));
          return words.every(w => searchText.includes(w));
        });

    // Sort — out of stock always at bottom (continue-policy items treated as out-of-stock for store locations)
    filtered.sort((a, b) => {
      const aOut = ((a.variant.inventoryQuantity || 0) <= 0 && (a.variant.inventoryPolicy !== 'continue' || locationType === 'store')) ? 1 : 0;
      const bOut = ((b.variant.inventoryQuantity || 0) <= 0 && (b.variant.inventoryPolicy !== 'continue' || locationType === 'store')) ? 1 : 0;
      if (aOut !== bOut) return aOut - bOut;

      switch (sortBy) {
        case 'name-asc':
          return a.product.title.localeCompare(b.product.title) || a.variant.title.localeCompare(b.variant.title);
        case 'name-desc':
          return b.product.title.localeCompare(a.product.title) || b.variant.title.localeCompare(a.variant.title);
        case 'price-asc':
          return parseFloat(a.variant.price) - parseFloat(b.variant.price);
        case 'price-desc':
          return parseFloat(b.variant.price) - parseFloat(a.variant.price);
        case 'stock-desc':
          return (b.variant.inventoryQuantity || 0) - (a.variant.inventoryQuantity || 0);
        default:
          return 0;
      }
    });

    // Log search to server (debounced)
    if (searchQuery && searchQuery.length >= 2) {
      logProductSearch(searchQuery, filtered.length);
    }

    return filtered;
  }, [allProducts, searchQuery, sortBy, logProductSearch, locationType]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const parseMattressDimensions = (variantTitle) => {
    const match3 = variantTitle.match(/(\d+)\s*x\s*(\d+)\s*x\s*(\d+)/);
    if (match3) return { length: parseInt(match3[1]), width: parseInt(match3[2]), height: parseInt(match3[3]) };
    const match2 = variantTitle.match(/(\d+)\s*x\s*(\d+)/);
    if (match2) return { length: parseInt(match2[1]), width: parseInt(match2[2]), height: null };
    return null;
  };

  const isMattressProduct = (product) => {
    const title = (product.title || '').toLowerCase();
    const customizableNames = [
      'original mattress',
      'hybrid latex mattress',
      'latex ortho mattress',
      'founder\'s mattress',
      'founders mattress',
      'ultima memory foam mattress',
      'ultima natural latex',
      'baby mattress',
      'baby natural latex mattress',
      'memory foam mattress topper',
      '2 inch memory foam mattress topper',
      'aeroflow mattress topper',
      'natural latex mattress topper',
      'cloud mattress'
    ];
    const excludeNames = ['tri fold mattress', 'tri-fold mattress'];
    if (excludeNames.some(name => title.includes(name))) return false;
    // Covers/protectors are handled by their own branch — never treat as customizable mattresses
    if (title.includes('cover') || title.includes('protector')) return false;
    const type = (product.productType || '').toLowerCase();
    // Robust: detect by product type (title wording varies in Shopify), fall back to the legacy name list
    if (type.includes('mattress') || type.includes('topper')) return true;
    return customizableNames.some(name => title.includes(name));
  };

  const isBabyMattress = (product) => {
    const title = (product.title || '').toLowerCase();
    return title.includes('baby mattress') || (title.includes('baby') && title.includes('mattress'));
  };

  // Length/width bounds for the custom-size modal, derived from the product's ACTUAL variants.
  // MAX = the largest available variant dimension (can't cover a size bigger than any real variant),
  // so a product whose widest size is 72 shows 72, not a hardcoded 78. MIN stays a business floor so
  // a custom size can still be smaller than the smallest standard variant (matched by the covering
  // rule). Falls back to the legacy hardcoded maxes only if no variant dimensions can be parsed.
  const dimBoundsForProduct = (product) => {
    const isBaby = isBabyMattress(product);
    const minL = isBaby ? 30 : 60;
    const minW = isBaby ? 20 : 24;
    const dims = (product?.variants || []).map(v => parseMattressDimensions(v.title)).filter(Boolean);
    const maxL = dims.length ? Math.max(...dims.map(d => d.length)) : (isBaby ? 52 : 84);
    const maxW = dims.length ? Math.max(...dims.map(d => d.width)) : (isBaby ? 28 : 78);
    return { minL, maxL, minW, maxW };
  };

  const isDogBedProduct = (product) => {
    const title = (product.title || '').toLowerCase();
    const type = (product.productType || '').toLowerCase();
    if (title.includes('cover')) return false;
    return title.includes('dog bed') || title.includes('pet bed') || type.includes('pet bed');
  };

  const handleAddToCart = async (product, variant) => {
    if ((variant.inventoryQuantity || 0) <= 0 && variant.inventoryPolicy !== 'continue') {
      alert('This item is out of stock');
      return;
    }

    // Block ALL continue-selling items (MTO and non-MTO) for store locations
    if (locationType === 'store' && variant.inventoryPolicy === 'continue' && (variant.inventoryQuantity || 0) <= 0) {
      alert('This item is out of stock at this store location');
      return;
    }

    // Check if this is a mattress with customizable dimensions (warehouse only — stores sell standard sizes)
    const dims = parseMattressDimensions(variant.title);
    const excludedProduct = ['tri fold mattress', 'tri-fold mattress'].some(name => (product.title || '').toLowerCase().includes(name));
    if (locationType !== 'store' && !excludedProduct && (isMattressProduct(product) || activeTab === 'mattresses') && dims) {
      setCustomizeProduct(product);
      setCustomizeVariant(variant);
      setCustomLength(dims.length);
      setCustomWidth(dims.width);
      setStandardDims(dims);
      setMatchedVariant(variant);
      setMatchedDims(dims);
      setShowCustomizeModal(true);
      // Fetch all variants (products can have 100+ variants, embedded list is capped at 100)
      try {
        const vRes = await api.get(`/products/${product.id}/variants`);
        const allVariants = vRes.data.variants || [];
        if (allVariants.length > product.variants.length) {
          const updated = { ...product, variants: allVariants };
          setCustomizeProduct(updated);
          const match = findMatchingVariant(updated, dims.length, dims.width, dims.height);
          if (match) { setMatchedVariant(match.variant); setMatchedDims(match.dims); }
        }
      } catch (e) { /* fallback to embedded variants */ }
      return;
    }

    // Check if this is a dog bed product (personalize modal — warehouse only)
    // Pet mats are not personalizable — exclude them even inside the Dog Beds category
    const isPetMat = /\bmats?\b/.test((product.title || '').toLowerCase());
    if (locationType !== 'store' && !isPetMat && (isDogBedProduct(product) || activeTab === 'petbed')) {
      setPersonalizeProduct(product);
      setPersonalizeVariant(variant);
      setPersonalizeName('');
      setWantsPersonalization(false);
      setShowPersonalizeModal(true);
      return;
    }

    const cartItem = {
      variantId: variant.id,
      productId: product.id,
      title: `${product.title}${variant.title !== 'Default Title' ? ' - ' + variant.title : ''}`,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice || null,
      quantity: 1,
      sku: variant.sku,
      image: variant.imageSrc || variant.image_src || (variant.image_id && product.images.find(img => img.id === variant.image_id)?.src) || product.images[0]?.src,
      inventoryQuantity: variant.inventoryQuantity || 0,
      inventoryPolicy: variant.inventoryPolicy || null,
      inventoryItemId: variant.inventoryItemId || null,
      locationType: locationType || 'store',
      locationId: locationId || null,
      locationName: locationName || ''
    };

    if (locationType !== 'store' && variant.inventoryPolicy === 'continue' && (variant.inventoryQuantity || 0) <= 0) {
      const titleLower = (product.title || '').toLowerCase();
      const isCoverProduct = titleLower.includes('dog bed cover') || titleLower.includes('mattress cover');

      if (isCoverProduct) {
        // Cover products: extract "Size - dims" from variant title
        // e.g. "Hybrid Latex / Double / 72 x 48 x 6" → "Double - 72 x 48 x 6"
        // e.g. "Mud Red / Extra Large - 54 x 36" → "Extra Large - 54 x 36"
        const vTitle = (variant.title || '').trim();
        // Match the last segment that contains "SizeName - digits x digits" or "SizeName / digits x digits"
        const sizeMatch = vTitle.match(/([A-Za-z][A-Za-z ]*?)\s*[-/]\s*(\d+\s*x\s*\d+(?:\s*x\s*\d+)?)\s*$/);
        const customSize = sizeMatch ? `${sizeMatch[1].trim()} - ${sizeMatch[2].trim()}` : vTitle;
        cartItem.customAttributes = [
          ...(cartItem.customAttributes || []),
          { key: '_custom_size', value: customSize }
        ];
      } else {
        try {
          const mtoRes = await api.get(`/products/variant/${variant.id}/is-mto`);
          if (mtoRes.data.isMto) {
            const variantDims = parseMattressDimensions(variant.title);
            cartItem.customAttributes = [
              ...(cartItem.customAttributes || []),
              { key: '_is_mto', value: '1' },
              ...(variantDims ? [{ key: '_custom_size', value: `${variantDims.length} x ${variantDims.width}${variantDims.height ? ' x ' + variantDims.height : ''}_ismto` }] : [])
            ];
          }
        } catch (e) { /* ignore MTO check failure */ }
      }
    }

    const result = await addToCart(cartItem);

    if (result.success) {
      setAddedVariantId(variant.id);
      setTimeout(() => setAddedVariantId(null), 1200);
    } else {
      alert(result.error || 'Failed to add to cart');
    }
  };

  // Find the nearest standard variant that covers the entered custom L x W.
  //
  // Always-true constraints:
  //   1. Height must match EXACTLY (no fallback to other heights).
  //   2. Candidate length  >= customer length.
  //   3. Candidate breadth >= customer breadth.
  //   4. Candidate area    >= customer's selected area (so an exact standard size matches itself).
  //
  // Selection: widen the tolerance in +3" steps on BOTH length & breadth, from +3 up to the largest
  // available size. The FIRST step that catches any covering variant wins; within that step pick the
  // smallest area (closest above). An exact size is caught in the first step. Returns null only if no
  // variant covers the size at all (custom larger than every variant on a dimension).
  const findMatchingVariant = (product, length, width, originalHeight) => {
    const inputArea = length * width;

    const covering = product.variants
      .map(v => ({ variant: v, dims: parseMattressDimensions(v.title) }))
      .filter(v => v.dims !== null)
      .filter(v =>
        (originalHeight == null || v.dims.height === originalHeight) && // 1. exact height
        v.dims.length >= length &&                                       // 2. length covers
        v.dims.width >= width &&                                         // 3. breadth covers
        (v.dims.length * v.dims.width) >= inputArea                      // 4. area >= (exact allowed)
      );

    if (covering.length === 0) return null;

    // Largest tolerance we'd ever need = the biggest single-dimension gap to a covering variant
    // (i.e. up to the max available length/width). Step +3" until a variant falls inside the band.
    const maxExcess = Math.max(...covering.map(v =>
      Math.max(v.dims.length - length, v.dims.width - width)
    ));
    for (let band = 3; band <= maxExcess + 3; band += 3) {
      const inBand = covering.filter(v =>
        v.dims.length <= length + band && v.dims.width <= width + band
      );
      if (inBand.length > 0) {
        // closest above = smallest area within this band
        inBand.sort((a, b) => (a.dims.length * a.dims.width) - (b.dims.length * b.dims.width));
        return inBand[0];
      }
    }
    return null; // no covering variant at all
  };

  const handleDimensionChange = (newLength, newWidth) => {
    if (!customizeProduct) return;
    const l = parseInt(newLength);
    const w = parseInt(newWidth);
    if (isNaN(l) || isNaN(w)) {
      setMatchedVariant(null);
      setMatchedDims(null);
      return;
    }
    const match = findMatchingVariant(customizeProduct, l, w, standardDims?.height);
    if (match) {
      setMatchedVariant(match.variant);
      setMatchedDims(match.dims);
    } else {
      setMatchedVariant(null);
      setMatchedDims(null);
    }
  };

  const handleCustomizedAddToCart = async () => {
    const length = parseInt(customLength);
    const width = parseInt(customWidth);

    const { minL, maxL, minW, maxW } = dimBoundsForProduct(customizeProduct);

    if (isNaN(length) || length < minL || length > maxL) {
      alert(`Length must be between ${minL} and ${maxL} inches`);
      return;
    }
    if (isNaN(width) || width < minW || width > maxW) {
      alert(`Width must be between ${minW} and ${maxW} inches`);
      return;
    }

    if (!matchedVariant || !matchedDims) {
      alert('No matching variant found for the entered dimensions. Please adjust.');
      return;
    }

    // Only attach custom attributes if dimensions differ from the matched variant
    const isCustomized = length !== matchedDims.length || width !== matchedDims.width;

    const cartItem = {
      variantId: matchedVariant.id,
      productId: customizeProduct.id,
      title: `${customizeProduct.title}${matchedVariant.title !== 'Default Title' ? ' - ' + matchedVariant.title : ''}`,
      price: matchedVariant.price,
      compareAtPrice: matchedVariant.compareAtPrice || null,
      quantity: 1,
      sku: matchedVariant.sku,
      image: matchedVariant.imageSrc || matchedVariant.image_src || (matchedVariant.image_id && customizeProduct.images.find(img => img.id === matchedVariant.image_id)?.src) || customizeProduct.images[0]?.src,
      inventoryQuantity: matchedVariant.inventoryQuantity || 0,
      inventoryPolicy: matchedVariant.inventoryPolicy || null,
      inventoryItemId: matchedVariant.inventoryItemId || null,
      locationType: locationType || 'store',
      locationId: locationId || null,
      locationName: locationName || ''
    };

    // Custom Length/Width/Height no longer added as line attributes
    // _custom_size attribute below handles dimension tracking

    if (locationType !== 'store' && matchedVariant.inventoryPolicy === 'continue' && (matchedVariant.inventoryQuantity || 0) <= 0) {
      try {
        const mtoRes = await api.get(`/products/variant/${matchedVariant.id}/is-mto`);
        if (mtoRes.data.isMto) {
          if (isCustomized) {
            cartItem.customAttributes = [
              ...(cartItem.customAttributes || []),
              { key: '_is_mto', value: '1' },
              { key: 'custom_size', value: `${length} x ${width}${matchedDims?.height ? ' x ' + matchedDims.height : ''} inches` }
            ];
          } else {
            cartItem.customAttributes = [
              ...(cartItem.customAttributes || []),
              { key: '_is_mto', value: '1' },
              { key: '_custom_size', value: `${length} x ${width}${matchedDims?.height ? ' x ' + matchedDims.height : ''}_ismto` }
            ];
          }
        } else if (isCustomized) {
          cartItem.customAttributes = [
            ...(cartItem.customAttributes || []),
            { key: 'custom_size', value: `${length} x ${width}${matchedDims?.height ? ' x ' + matchedDims.height : ''} inches` }
          ];
        }
      } catch (e) {
        if (isCustomized) {
          cartItem.customAttributes = [
            ...(cartItem.customAttributes || []),
            { key: 'custom_size', value: `${length} x ${width}${matchedDims?.height ? ' x ' + matchedDims.height : ''} inches` }
          ];
        }
      }
    } else if (isCustomized) {
      cartItem.customAttributes = [
        ...(cartItem.customAttributes || []),
        { key: 'custom_size', value: `${length} x ${width}${matchedDims?.height ? ' x ' + matchedDims.height : ''} inches` }
      ];
    }

    const result = await addToCart(cartItem);

    if (result.success) {
      setShowCustomizeModal(false);
      setAddedVariantId(customizeVariant.id);
      setTimeout(() => setAddedVariantId(null), 1200);
    } else {
      alert(result.error || 'Failed to add to cart');
    }
  };

  return (
    <div>
      {/* Search + Sort bar */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '16px', pointerEvents: 'none' }}>&#128269;</span>
          <input
            type="text"
            placeholder="Search by name, variant, SKU, or barcode..."
            value={searchQuery}
            onChange={handleSearchChange}
            style={{
              width: '100%',
              padding: '10px 36px 10px 36px',
              border: '2px solid rgba(200, 131, 70, 0.12)',
              borderRadius: '10px',
              fontSize: '14px',
              transition: 'border-color 0.2s',
              outline: 'none',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#c88346'}
            onBlur={(e) => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
          />
          {searchQuery && (
            <button
              onClick={() => { handleSearchChange({ target: { value: '' } }); }}
              style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', padding: '2px 4px', lineHeight: 1 }}
              title="Clear search"
            >✕</button>
          )}
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            padding: '10px 12px',
            border: '2px solid rgba(200, 131, 70, 0.12)',
            borderRadius: '10px',
            fontSize: '13px',
            color: '#3d2b1f',
            background: '#ffffff',
            cursor: 'pointer',
            minWidth: '160px',
            outline: 'none'
          }}
          onFocus={(e) => e.target.style.borderColor = '#c88346'}
          onBlur={(e) => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
        >
          {SORT_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Collection tabs */}
      <div style={{
        display: 'flex',
        gap: '6px',
        marginBottom: '16px',
        overflowX: 'auto',
        paddingBottom: '4px',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        {COLLECTION_TABS.map(tab => (
          <button
            key={tab.handle}
            onClick={() => handleTabChange(tab.handle)}
            style={{
              padding: '6px 14px',
              border: activeTab === tab.handle ? '2px solid #c88346' : '1px solid rgba(200, 131, 70, 0.12)',
              borderRadius: '14px',
              fontSize: '12px',
              fontWeight: activeTab === tab.handle ? '700' : '500',
              color: activeTab === tab.handle ? '#fff' : '#7a6b5d',
              background: activeTab === tab.handle ? '#c88346' : '#f8f4ef',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Result count */}
      {!loading && flatItems.length > 0 && (
        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>
          {flatItems.length} item{flatItems.length !== 1 ? 's' : ''}
          {inventoryLoading && <span style={{ marginLeft: '8px', color: '#60a5fa' }}>⟳ updating stock...</span>}
        </div>
      )}

      {loading && flatItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: '28px', marginBottom: '8px', animation: 'spin 1s linear infinite' }}>&#9696;</div>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Loading products...
        </div>
      )}

      {!loading && flatItems.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>&#128722;</div>
          {searchQuery ? 'No products found' : 'No products available'}
        </div>
      )}

      {/* Product grid — one card per variant */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: '14px',
        position: 'relative',
        opacity: loading ? 0.4 : 1,
        pointerEvents: loading ? 'none' : 'auto',
        transition: 'opacity 0.15s ease'
      }}>
        {flatItems.map(({ product, variant }) => {
          const inventoryPending = variant.inventoryQuantity === null;
          const isMtoVariant = !inventoryPending && variant.inventoryPolicy === 'continue' && (variant.inventoryQuantity || 0) <= 0 && !!mtoMap[String(variant.id)];
          // For store locations, ALL continue-selling items with 0 stock should appear as out of stock
          const outOfStock = !inventoryPending && (variant.inventoryQuantity || 0) <= 0 && (variant.inventoryPolicy !== 'continue' || locationType === 'store');
          const isMtoAtZeroInventory = isMtoVariant && locationType !== 'store';
          const justAdded = addedVariantId === variant.id;

          return (
            <div
              key={variant.id}
              onClick={() => !outOfStock && handleAddToCart(product, variant)}
              style={{
                border: justAdded ? '2px solid #27ae60' : '1px solid rgba(200, 131, 70, 0.12)',
                borderRadius: '16px',
                cursor: outOfStock ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
                background: outOfStock ? 'rgba(245, 240, 235, 0.6)' : 'white',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                opacity: outOfStock ? 0.6 : 1,
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!outOfStock) {
                  e.currentTarget.style.borderColor = '#c88346';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px #f5e6d3';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = justAdded ? '#27ae60' : 'rgba(200, 131, 70, 0.12)';
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {justAdded && (
                <div style={{
                  position: 'absolute', top: '8px', right: '8px', background: '#27ae60',
                  color: 'white', fontSize: '11px', fontWeight: '700', padding: '3px 8px',
                  borderRadius: '10px', zIndex: 2
                }}>
                  &#10003; Added
                </div>
              )}

              {isMtoAtZeroInventory && (
                <div style={{
                  position: 'absolute', top: '8px', left: '8px',
                  background: '#7c3aed', color: 'white',
                  fontSize: '10px', fontWeight: '700', padding: '2px 7px',
                  borderRadius: '5px', zIndex: 2, letterSpacing: '0.5px'
                }}>
                  MTO
                </div>
              )}

              <div style={{
                width: '100%', height: '140px', background: '#f0ebe5',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden', flexShrink: 0
              }}>
                {(() => {
                  const variantImage = variant.image_id && product.images.find(img => img.id === variant.image_id);
                  const imgSrc = variant.imageSrc || variant.image_src || (variantImage ? variantImage.src : product.images[0]?.src);
                  return imgSrc ? (
                    <img
                      src={imgSrc}
                      alt={product.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      loading="lazy"
                    />
                  ) : (
                    <span style={{ fontSize: '36px', color: '#c0b8ae' }}>&#128247;</span>
                  );
                })()}
              </div>

              <div style={{ padding: '10px 12px 12px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <h3 style={{
                  fontSize: '13px', fontWeight: '600', color: '#3d2b1f',
                  marginBottom: '2px', lineHeight: '1.3',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                }}>
                  {product.title}
                </h3>

                {variant.title !== 'Default Title' && (
                  <p style={{ fontSize: '11px', color: '#7a6b5d', marginBottom: '2px', fontWeight: '500' }}>
                    {variant.title}
                  </p>
                )}

                {variant.sku && (
                  <p style={{ fontSize: '10px', color: '#a69889', marginBottom: '6px' }}>
                    {variant.sku}
                  </p>
                )}

                <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <div>
                    {variant.compareAtPrice && parseFloat(variant.compareAtPrice) > parseFloat(variant.price) && (
                      <div style={{ fontSize: '11px', color: '#a69889', textDecoration: 'line-through', lineHeight: 1 }}>
                        {formatCurrency(variant.compareAtPrice)}
                      </div>
                    )}
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#3d2b1f', lineHeight: 1.2 }}>
                      {formatCurrency(variant.price)}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '10px', fontWeight: '600',
                    color: inventoryPending ? '#94a3b8' : outOfStock ? '#c0392b' : '#27ae60',
                    background: inventoryPending ? '#f1f5f9' : outOfStock ? '#fdeaea' : '#eaf7ee',
                    padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap'
                  }}>
                    {inventoryPending ? '⟳' : outOfStock ? 'Out of stock' : isMtoAtZeroInventory ? 'MTO' : (variant.inventoryPolicy === 'continue' && (variant.inventoryQuantity || 0) <= 0) ? (locationType === 'store' ? 'Out of stock' : 'Available') : `${variant.inventoryQuantity} in stock`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dog Bed Personalization Modal */}
      {showPersonalizeModal && personalizeProduct && personalizeVariant && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 15, 30, 0.55)',  display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowPersonalizeModal(false)}
        >
          <div
            style={{
              background: '#ffffff', borderRadius: '16px', padding: '28px',
              width: '420px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(26, 26, 46, 0.2), 0 8px 24px rgba(26, 26, 46, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#3d2b1f', marginBottom: '4px' }}>
              Personalize Your Dog Bed
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
              {personalizeProduct.title}{personalizeVariant.title !== 'Default Title' ? ` \u2014 ${personalizeVariant.title}` : ''}
            </p>

            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: '16px', cursor: 'pointer'
            }}
              onClick={() => setWantsPersonalization(!wantsPersonalization)}
            >
              <div style={{
                width: '20px', height: '20px', borderRadius: '4px',
                border: `2px solid ${wantsPersonalization ? '#c88346' : '#d1d5db'}`,
                background: wantsPersonalization ? '#c88346' : 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s', flexShrink: 0
              }}>
                {wantsPersonalization && (
                  <span style={{ color: 'white', fontSize: '13px', fontWeight: '700' }}>✓</span>
                )}
              </div>
              <div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#3d2b1f' }}>
                  Add personalization
                </span>
                <span style={{ fontSize: '12px', color: '#c88346', marginLeft: '8px', fontWeight: '600' }}>
                  +{formatCurrency('199')}
                </span>
              </div>
            </div>

            {wantsPersonalization && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                  Name on bed <span style={{ color: '#9ca3af', fontWeight: '400' }}>(max 10 characters)</span>
                </label>
                <input
                  type="text"
                  maxLength={10}
                  value={personalizeName}
                  onChange={e => setPersonalizeName(e.target.value.replace(/[^a-zA-Z ]/g, ''))}
                  placeholder="Enter name"
                  style={{
                    width: '100%', padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box', letterSpacing: '1px'
                  }}
                  onFocus={e => e.target.style.borderColor = '#c88346'}
                  onBlur={e => e.target.style.borderColor = 'rgba(200, 131, 70, 0.12)'}
                  autoFocus
                />
                <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px', textAlign: 'right' }}>
                  {personalizeName.length}/10
                </div>
              </div>
            )}

            <div style={{
              background: '#f5f0eb', borderRadius: '10px', padding: '12px 16px',
              marginBottom: '20px', fontSize: '13px', color: '#7a6b5d'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span>Product price</span>
                <span style={{ fontWeight: '600' }}>{formatCurrency(personalizeVariant.price)}</span>
              </div>
              {wantsPersonalization && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span>Personalization fee</span>
                  <span style={{ fontWeight: '600' }}>{formatCurrency('199')}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(200, 131, 70, 0.12)', paddingTop: '8px', marginTop: '4px' }}>
                <span style={{ fontWeight: '700' }}>Total</span>
                <span style={{ fontWeight: '700' }}>
                  {formatCurrency(parseFloat(personalizeVariant.price) + (wantsPersonalization ? 199 : 0))}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={async () => {
                  if (wantsPersonalization && !personalizeName.trim()) {
                    alert('Please enter a name for personalization');
                    return;
                  }

                  const cartItem = {
                    variantId: personalizeVariant.id,
                    productId: personalizeProduct.id,
                    title: `${personalizeProduct.title}${personalizeVariant.title !== 'Default Title' ? ' - ' + personalizeVariant.title : ''}`,
                    price: personalizeVariant.price,
                    compareAtPrice: personalizeVariant.compareAtPrice || null,
                    quantity: 1,
                    sku: personalizeVariant.sku,
                    image: personalizeVariant.imageSrc || personalizeVariant.image_src || (personalizeVariant.image_id && personalizeProduct.images.find(img => img.id === personalizeVariant.image_id)?.src) || personalizeProduct.images[0]?.src,
                    inventoryQuantity: personalizeVariant.inventoryQuantity || 0,
                    inventoryPolicy: personalizeVariant.inventoryPolicy || null,
                    inventoryItemId: personalizeVariant.inventoryItemId || null,
                    locationType: locationType || 'store',
                    locationId: locationId || null,
                    locationName: locationName || ''
                  };

                  if (wantsPersonalization) {
                    const name = personalizeName.trim();
                    const initialsVal = `${name} ( + ₹199 included )`;
                    // Extract size from variant title (e.g. "Grey / Extra Large - 54 x 36" → "Extra Large - 54 x 36")
                    const vTitle = (personalizeVariant.title || '').trim();
                    const sizeMatch = vTitle.match(/([A-Za-z][A-Za-z ]*?)\s*[-/]\s*(\d+\s*x\s*\d+(?:\s*x\s*\d+)?)\s*$/);
                    const customSizeVal = sizeMatch ? `${sizeMatch[1].trim()} - ${sizeMatch[2].trim()}` : vTitle;
                    cartItem.customAttributes = [
                      { key: '_custom_size', value: customSizeVal },
                      { key: '_personalization_initials', value: initialsVal },
                      { key: '_personalize_price', value: '199' },
                      { key: 'Initials', value: initialsVal }
                    ];
                    cartItem.personalizationFee = 199;
                  }

                  if (locationType !== 'store' && personalizeVariant.inventoryPolicy === 'continue' && (personalizeVariant.inventoryQuantity || 0) <= 0) {
                    try {
                      const mtoRes = await api.get(`/products/variant/${personalizeVariant.id}/is-mto`);
                      if (mtoRes.data.isMto) {
                        const variantDims = parseMattressDimensions(personalizeVariant.title);
                        cartItem.customAttributes = [
                          ...(cartItem.customAttributes || []),
                          { key: '_is_mto', value: '1' },
                          ...(variantDims ? [{ key: '_custom_size', value: `${variantDims.length} x ${variantDims.width}${variantDims.height ? ' x ' + variantDims.height : ''}_ismto` }] : [])
                        ];
                      }
                    } catch (e) { /* ignore MTO check failure */ }
                  }

                  const result = await addToCart(cartItem);
                  if (result.success) {
                    setShowPersonalizeModal(false);
                    setAddedVariantId(personalizeVariant.id);
                    setTimeout(() => setAddedVariantId(null), 1200);
                  } else {
                    alert(result.error || 'Failed to add to cart');
                  }
                }}
                style={{
                  flex: 1, padding: '12px', background: '#c88346', color: 'white',
                  border: 'none', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '700', cursor: 'pointer'
                }}
              >
                Add to Cart
              </button>
              <button
                onClick={() => setShowPersonalizeModal(false)}
                style={{
                  padding: '12px 20px', background: '#f5f0eb', color: '#3d2b1f',
                  border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      , document.body)}

      {/* Mattress Customization Modal */}
      {showCustomizeModal && customizeProduct && customizeVariant && standardDims && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 15, 30, 0.55)',  display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000
          }}
          onClick={() => setShowCustomizeModal(false)}
        >
          <div
            style={{
              background: '#ffffff', borderRadius: '16px', padding: '28px',
              width: '440px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(26, 26, 46, 0.2), 0 8px 24px rgba(26, 26, 46, 0.1)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#3d2b1f', marginBottom: '4px' }}>
              Customize {(customizeProduct.title || '').toLowerCase().includes('topper') ? 'Topper' : 'Mattress'} Size
            </h3>
            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px' }}>
              {customizeProduct.title} — {customizeVariant.title}
            </p>

            <div style={{
              background: '#f5f0eb', borderRadius: '10px', padding: '12px 16px',
              marginBottom: '20px', fontSize: '13px', color: '#7a6b5d'
            }}>
              Selected size: <strong>{standardDims.length} x {standardDims.width}{standardDims.height ? ` x ${standardDims.height}` : ''}</strong>
              <span style={{ color: '#9ca3af', marginLeft: '8px' }}>{standardDims.height ? '(L x W x H)' : '(L x W)'}</span>
            </div>

            {(() => {
              const { minL, maxL, minW, maxW } = dimBoundsForProduct(customizeProduct);
              const l = parseInt(customLength), w = parseInt(customWidth);
              const lValid = !isNaN(l) && l >= minL && l <= maxL;
              const wValid = !isNaN(w) && w >= minW && w <= maxW;
              const lHasValue = customLength !== '' && customLength !== undefined;
              const wHasValue = customWidth !== '' && customWidth !== undefined;
              return (
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                  Length <span style={{ color: '#9ca3af', fontWeight: '400' }}>({minL}–{maxL})</span>
                </label>
                <input
                  type="number"
                  min={minL}
                  max={maxL}
                  step={1}
                  value={customLength}
                  onChange={e => {
                    setCustomLength(e.target.value);
                    handleDimensionChange(e.target.value, customWidth);
                  }}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `2px solid ${lHasValue ? (lValid ? '#22c55e' : '#ef4444') : 'rgba(200, 131, 70, 0.12)'}`,
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box',
                    color: lHasValue ? (lValid ? '#166534' : '#dc2626') : '#3d2b1f'
                  }}
                />
                {lHasValue && !lValid && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', fontWeight: '600' }}>
                    Must be {minL}–{maxL} inches
                  </div>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                  Width <span style={{ color: '#9ca3af', fontWeight: '400' }}>({minW}–{maxW})</span>
                </label>
                <input
                  type="number"
                  min={minW}
                  max={maxW}
                  step={1}
                  value={customWidth}
                  onChange={e => {
                    setCustomWidth(e.target.value);
                    handleDimensionChange(customLength, e.target.value);
                  }}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: `2px solid ${wHasValue ? (wValid ? '#22c55e' : '#ef4444') : 'rgba(200, 131, 70, 0.12)'}`,
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box',
                    color: wHasValue ? (wValid ? '#166534' : '#dc2626') : '#3d2b1f'
                  }}
                />
                {wHasValue && !wValid && (
                  <div style={{ fontSize: '11px', color: '#dc2626', marginTop: '4px', fontWeight: '600' }}>
                    Must be {minW}–{maxW} inches
                  </div>
                )}
              </div>

              <div style={{ width: '80px', display: standardDims.height ? 'block' : 'none' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#3d2b1f', marginBottom: '6px' }}>
                  Height
                </label>
                <input
                  type="number"
                  value={standardDims.height || ''}
                  disabled
                  style={{
                    width: '100%', padding: '10px 14px', border: '2px solid rgba(200, 131, 70, 0.12)',
                    borderRadius: '10px', fontSize: '15px', fontWeight: '600',
                    outline: 'none', boxSizing: 'border-box',
                    background: '#f3f4f6', color: '#9ca3af'
                  }}
                />
              </div>
            </div>
              );
            })()}

            {/* Matched variant info */}
            {matchedVariant && matchedDims ? (
              <div style={{
                background: '#eaf7ee', borderRadius: '10px', padding: '12px 16px',
                marginBottom: '20px', fontSize: '13px', color: '#166534'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  ✓ Matched variant: {matchedVariant.title}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Size: {matchedDims.length} x {matchedDims.width}{matchedDims.height ? ` x ${matchedDims.height}` : ''}</span>
                  <span style={{ fontWeight: '700' }}>Price: {formatCurrency(matchedVariant.price)}</span>
                </div>
              </div>
            ) : (
              <div style={{
                background: '#fee2e2', borderRadius: '10px', padding: '12px 16px',
                marginBottom: '20px', fontSize: '13px', color: '#dc2626'
              }}>
                No matching variant found for the entered dimensions. Try smaller values.
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={handleCustomizedAddToCart}
                disabled={!matchedVariant}
                style={{
                  flex: 1, padding: '12px', background: matchedVariant ? '#c88346' : '#d1d5db',
                  color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '700', cursor: matchedVariant ? 'pointer' : 'not-allowed'
                }}
              >
                Add to Cart
              </button>
              <button
                onClick={() => setShowCustomizeModal(false)}
                style={{
                  padding: '12px 20px', background: '#f5f0eb', color: '#3d2b1f',
                  border: '1px solid rgba(200, 131, 70, 0.12)', borderRadius: '10px', fontSize: '14px',
                  fontWeight: '600', cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      , document.body)} 
    </div>
  );
}

export default ProductSearch;
